import React, { Component, Suspense, lazy } from "react";
import { useLocation } from "react-router-dom";

const RELOAD_FLAG = "haechie:chunk-reload";

/**
 * React.lazy page with `preload()`. A deploy deletes the previous build's chunks, so a tab opened
 * before it fails to load them; that case reloads the page once to pick up the new build.
 */
export const lazyPage = (loader) => {
  let pending;
  const load = (reloadOnFail) => {
    if (!pending) {
      pending = loader().catch((error) => {
        pending = undefined;
        throw error;
      });
    }
    return pending.then(
      (mod) => {
        sessionStorage.removeItem(RELOAD_FLAG);
        return mod;
      },
      (error) => {
        if (reloadOnFail && !sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, "1");
          window.location.reload();
          return new Promise(() => {});
        }
        throw error;
      }
    );
  };
  const Page = lazy(() => load(true));
  Page.preload = () => load(false).catch(() => {});
  return Page;
};

/** Loads pages one at a time while the browser is idle, so opening them later is instant. */
export const preloadPagesWhenIdle = (pages) => {
  const queue = [...pages];
  let handle = 0;
  const idle = window.requestIdleCallback
    ? (fn) => window.requestIdleCallback(fn, { timeout: 4000 })
    : (fn) => window.setTimeout(fn, 1500);
  const cancel = window.cancelIdleCallback || window.clearTimeout;
  const next = () => {
    const page = queue.shift();
    if (!page) return;
    page.preload().finally(() => {
      handle = idle(next);
    });
  };
  handle = idle(next);
  return () => {
    queue.length = 0;
    cancel(handle);
  };
};

const PageLoading = () => (
  <div className="flex min-h-[50vh] items-center justify-center" role="status" aria-label="Loading page">
    <span className="h-7 w-7 animate-spin rounded-full border-2 border-slate-400/30 border-t-[#7c8cff]" />
  </div>
);

class PageErrorBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-center">
        <p className="text-[14px] font-semibold">This page could not be loaded.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-lg bg-[#5b4dff] px-4 py-2 text-[13px] font-bold text-white"
        >
          Reload
        </button>
      </div>
    );
  }
}

/** Suspense + error boundary for one routed page, so the layout stays visible while it loads. */
export const PageSlot = ({ children }) => {
  const { pathname } = useLocation();
  return (
    <PageErrorBoundary key={pathname}>
      <Suspense fallback={<PageLoading />}>{children}</Suspense>
    </PageErrorBoundary>
  );
};
